import React, { FC } from 'react';
import { SectionHeader } from '@modernhealth/vital-components';

import { ContentCollectionRenderComponent } from 'src/generated/webapp_gql';
import { mapContentLinkToSectionHeaderProps } from 'src/modules/dashboard/helpers/contentLinkNodeToSectionHeader';

import { HorizontalContentCard } from './HorizontalContentCard';
import { VerticalContentCard } from './VerticalContentCard';
import { BannerContentCard } from './BannerContentCard';
import { TileContentCard } from './TileContentCard';
import { ContentLinkFragment, ScreenSectionFragment } from '../documents/getHomeScreen.generated';
import * as Sentry from '@sentry/browser';
import { CareContentCard } from 'src/modules/dashboard/components/CareContentCard';
import styled from '@emotion/styled';
import { useAnalytics } from '@modernhealth/web-analytics';
import { captureException } from 'src/shared/helpers/captureException';

export interface ContentCardProps {
    links: ContentLinkFragment[];
}

// TODO: Add line limits to ActionItem, this is a
// temporary solution which limits all <p> tags
// within ActionItemList sections to 2 lines to
// ensure the layout is not broken by long content.
const SectionWithLineLimits = styled.section({
    p: {
        overflow: 'hidden',
        display: '-webkit-box',
        textOverflow: 'ellipsis',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    },
});

export const RenderScreenSectionNode: FC<{
    section: ScreenSectionFragment | null;
}> = ({ section }) => {
    const { trackClick } = useAnalytics();

    if (!section) return null;

    const renderContent = () => {
        switch (section.renderComponent) {
            case ContentCollectionRenderComponent.Horizontal:
                return (
                    <HorizontalContentCard
                        links={section.contentCollectionLinks}
                        section={{ title: section.title, id: section.uuid }}
                    />
                );
            case ContentCollectionRenderComponent.Vertical:
                return (
                    <SectionWithLineLimits>
                        <VerticalContentCard
                            links={section.contentCollectionLinks}
                            section={{ title: section.title, id: section.uuid }}
                        />
                    </SectionWithLineLimits>
                );
            case ContentCollectionRenderComponent.Banner:
                return (
                    <BannerContentCard
                        links={section.contentCollectionLinks}
                        section={{ title: section.title, id: section.uuid }}
                    />
                );
            case ContentCollectionRenderComponent.Tile:
                return <TileContentCard links={section.contentCollectionLinks} />;
            case ContentCollectionRenderComponent.CareCard:
                return <CareContentCard links={section.contentCollectionLinks} />;
            default:
                Sentry.captureException(
                    `RenderComponent type: ${section.renderComponent} is not recognized`,
                );
                captureException(new Error('RenderComponent type is not recognized'), {
                    type: section.renderComponent,
                });
                return null;
        }
    };

    return (
        <>
            <SectionHeader
                title={section.title}
                {...mapContentLinkToSectionHeaderProps({
                    link: section.ctaLink,
                    secondaryText: section.ctaLinkText,
                    tracking: {
                        eventName: 'view all',
                        props: {
                            page_section: section.title,
                            page_section_uuid: section.uuid,
                            button_copy: section.ctaLinkText,
                        },
                        trackClick,
                    },
                })}
            />

            {renderContent()}
        </>
    );
};
